/**
 * Runtime reporter for missing translation keys.
 *
 * `@ardoq/i18n` cannot depend on `@ardoq/logging` (logging depends on
 * `@ardoq/common-helpers`, which depends on `@ardoq/i18n`), so the app
 * bootstrap registers a real reporter that forwards to the shared
 * frontend log channel. Until then, the reporter is a no-op.
 *
 * Misses are de-duplicated per (locale, namespace, key) for the session.
 * The set is in-memory only and resets on page reload — that is the
 * intended session semantics.
 */

export type MissingTranslationKey = {
  locale: string;
  namespace: string;
  key: string;
};

export type MissingKeyReporter = (info: MissingTranslationKey) => void;

const noopReporter: MissingKeyReporter = () => {};

let activeReporter: MissingKeyReporter = noopReporter;

const reportedKeys = new Set<string>();

const buildDedupeKey = ({
  locale,
  namespace,
  key,
}: MissingTranslationKey): string => `${locale}:${namespace}:${key}`;

export const registerMissingKeyReporter = (
  reporter: MissingKeyReporter
): void => {
  activeReporter = reporter;
};

/**
 * Report a missing translation key. Safe to call from hot paths:
 * - Deduplicated per (locale, namespace, key) for the session
 * - Reporter invocation is wrapped in try/catch so failures cannot
 *   corrupt translation resolution
 */
export const reportMissingTranslationKey = (info: MissingTranslationKey) => {
  const dedupeKey = buildDedupeKey(info);
  if (reportedKeys.has(dedupeKey)) return;
  reportedKeys.add(dedupeKey);
  try {
    activeReporter(info);
  } catch {
    // Swallow — a broken log channel must never break translation.
  }
};

/**
 * Test-only: clear the dedupe set and reporter registration. Not exported
 * from the package entry; imported directly by tests.
 */
export const _resetMissingKeyReporterForTests = (): void => {
  reportedKeys.clear();
  activeReporter = noopReporter;
};

/**
 * Reports a broken interpolation: a translated string that still contains
 * `{{placeholder}}` tokens after parameter substitution. This indicates
 * either placeholder-name drift between the backend (which provides the
 * values) and the frontend translation template, or that the backend sent
 * no details at all for a code whose template expects them.
 *
 * Only `detailKeys` (not values) cross the package boundary, so the
 * payload cannot leak user data regardless of which reporter is
 * registered. `cause` distinguishes drift from missing-details so the
 * noisier bucket doesn't drown the signal worth chasing.
 *
 * Dedupe is per (locale, code) for the session.
 */
export type BrokenInterpolation = {
  locale: string;
  code: string;
  detailKeys: string[];
  cause: 'placeholder-mismatch' | 'missing-details';
};

export type BrokenInterpolationReporter = (info: BrokenInterpolation) => void;

const noopBrokenInterpolationReporter: BrokenInterpolationReporter = () => {};

let activeBrokenInterpolationReporter: BrokenInterpolationReporter =
  noopBrokenInterpolationReporter;

const reportedBroken = new Set<string>();

export const registerBrokenInterpolationReporter = (
  reporter: BrokenInterpolationReporter
): void => {
  activeBrokenInterpolationReporter = reporter;
};

export const reportBrokenInterpolation = (info: BrokenInterpolation): void => {
  const dedupeKey = `${info.locale}:${info.code}`;
  if (reportedBroken.has(dedupeKey)) return;
  reportedBroken.add(dedupeKey);
  try {
    activeBrokenInterpolationReporter(info);
  } catch {
    // Swallow — a broken reporter must never break rendering.
  }
};

/**
 * Test-only: clear the dedupe set and reporter registration. Not exported
 * from the package entry; imported directly by tests.
 */
export const _resetBrokenInterpolationReporterForTests = (): void => {
  reportedBroken.clear();
  activeBrokenInterpolationReporter = noopBrokenInterpolationReporter;
};
